import { getEnvironmentName } from "lib/environment";
import { RemoteAddressType, SingleBusinessAddressType } from "types";
import { ApplicationTypeEnum } from "types/generated/operations";

type Environments =
  | "production"
  | "development"
  | "staging"
  | "test"
  | "secure";

export const convertStringIntoBool = (
  value: string | undefined
): boolean | undefined => {
  if (value === "Yes") return true;
  if (value === "No") return false;
};

export const convertBoolIntoString = (
  value: boolean | undefined | null
): string => {
  let returnVal = "";
  const userHasInput = value !== undefined && value !== null;
  if (userHasInput) {
    returnVal = value ? "Yes" : "No";
  }
  return returnVal;
};

export const ConvertToISODate = (date: string): string => {
  const isoDateTime = ConvertToISODateTime(date);
  return isoDateTime.split("T")[0];
};

export const ConvertToISODateTime = (date: string): string => {
  if (isNaN(Date.parse(date))) {
    return "";
  }
  return new Date(date).toISOString();
};

export const getStepNumberFromURL = (): number => {
  const url = window.location.pathname;
  const urlArray = url
    .split("?")[0]
    .split("/")
    .filter((item) => item);
  return Number(urlArray[urlArray.length - 1]);
};

/**
 * Converts an ISO8601 formatted date string into a typical USA format of mm/DD/yyyy
 * This does not allow for weekly date formats that are ISO8601 compliant and ignores any time component on the date string
 * If the string is not a valid date string or in an unexpected format the empty string will be returned.
 * This method assumes the date string provided is a valid date if in the correct format
 * @param date The date to convert
 * @returns Converted date or the empty string if conversion is not possible
 */
export const ConvertToValidDate = (date: string): string => {
  if (!date) return "";
  const iso8601Format =
    /^(?<year>\d{4})-(?<month>\d{2})-(?<day>\d{2})(?:T(?<time>[01]\d:[0-5]\d:[0-5]\d))?/;

  const matches = iso8601Format.exec(date);
  if (!matches) {
    console.error(`Invalid date detected: ${date}`);
    return "";
  }

  if (matches.groups?.time) {
    console.warn(`Date includes time component: ${date}`);
  }

  return `${matches.groups?.month}/${matches.groups?.day}/${matches.groups?.year}`;
};

export const getCWRedirectUrl = (url: string): string => {
  let environmentName: Environments = getEnvironmentName();
  if (environmentName === "development")
    return `${process.env.REACT_APP_CLOCKWORK_WEB_HOST}${url}`;
  const urlHost = process.env.REACT_APP_CLOCKWORK_WEB_HOST;
  return `${urlHost}${url}`;
};

export const getWWRedirectUrl = (url: string): string => {
  const environmentName: Environments = getEnvironmentName();
  if (environmentName === "test" || environmentName === undefined)
    return `http://test.host:3010${url}`;
  if (environmentName === "development")
    return `${process.env.REACT_APP_WISTERIA_WOMBAT_HOST}${url}`;
  const urlHost = process.env.REACT_APP_WISTERIA_WOMBAT_HOST;
  return `${urlHost}${url}`;
};

export const getNeonRedirectUrl = (url: string): string => {
  const urlHost = process.env.REACT_APP_NEON_DASH_HOST;
  return `${urlHost}${url}`;
};

export const convertToTitleCase = (inputString?: string | null): string => {
  if (!inputString) return "";
  const upperCase =
    inputString[0].toUpperCase() + inputString.slice(1).toLowerCase();
  return upperCase.replace(/_/g, "-");
};

export const getOfficeFullAddress = (
  officeLocation: SingleBusinessAddressType | RemoteAddressType
) => {
  if (!officeLocation) return "";

  const addresses =
    officeLocation.address1 +
    (officeLocation.address2 ? `, ${officeLocation.address2},` : ",");

  const locationName =
    "locationName" in officeLocation ? officeLocation.locationName : "";

  const fullAddress = `${addresses} ${officeLocation.city}, ${officeLocation.state} ${officeLocation.zip}`;

  if (locationName) {
    return `${locationName} - ${fullAddress}`;
  }

  return fullAddress;
};

export const hasQueryParam = (param: string): boolean => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.has(param);
};

export const errorMessageMap: { [key: string]: string } = {
  // eslint-disable-next-line no-useless-escape
  "yearFounded must be greater than or equal to 1900":
    "Head back to the first step, Business details, and tell us what year your company was founded.",
  'Failed to update prospect: ["Ein has already been taken"]':
    "Double check that EIN and try again",
  "You can not delete an employee that has dependents tied to it.":
    "Please remove all dependents before removing the employee",
  "You can not delete a COBRA employee that has dependents tied to them.":
    "Please remove all dependents before removing the employee",
  "You must list dependents immediately after their employees":
    "You must list dependents immediately after their employees",
  "Uploaded documents must have .pdf extension": "PDFs only, please",
  // eslint-disable-next-line no-useless-escape
  'Failed to create document: ["File name is invalid"]':
    "Double check your file. It must be a PDF, < 5MB and the name must be < 250 characters with no special characters besides _ or - ",
  "You are not authorized to access this data":
    "You’re not authorized to access this data. You might need to log in and try again.",
  "No file provided": "There was a problem with the PDF. Please try again.",
  "Empty files are not accepted":
    "That PDF doesn’t have any data inside. Please try a different PDF.",
  "Files must be 5MB or smaller": "PDFs need to be under 5MB.",
  "Files must be 10MB or smaller": "PDFs need to be under 10MB.",
  "File must be a PDF file-type": "PDFs only, please",
  "Uploaded document must have a filename less than 254 characters including extension":
    "That file name is too long. Keep it under 250 characters.",
  "Date must be today or in the future":
    "Great scott! Please pick today or a date in the future.",
  // eslint-disable-next-line no-useless-escape
  'Failed to update office location: ["Location name has already been taken"]':
    "Please enter a new location. We’ve already got that one on file.",
  "Failed to delete office location: Contains Employees":
    "Employees/owners are attached to this office location. First, remove the employee/owner from that location. Then you’ll be able to delete this location.",
  "You can not delete the first employee census of a prospect.":
    "You can not delete your first employee / owner.",
  "Application validation failed. Cannot continue to next step due to missing fields.":
    "Looks like you missed some info in the form. Double check you got all the required fields.",
  "Application validation failed for Application. Cannot continue to next step due to missing Document.":
    "Looks like you missed some info in the form. Double check you got all the required fields.",
  "Application validation failed. Cannot continue to next step due to missing record.":
    "Looks like you missed some info in the form. Double check you got all the required fields.",
};

export const isTestUser = window.location.pathname.includes("test-user-flow");

export const getAppType = (): ApplicationTypeEnum => {
  const splitPath = window.location.pathname.split("/");
  const basePath = splitPath[1] || "";
  switch (basePath) {
    case "health-insurance-application":
    case "pre-qualification":
    case "census-and-current-information":
    case "health-application-status":
      return "HEALTH_INSURANCE" as ApplicationTypeEnum;
    case "health-insurance-application-mdv":
    case "pre-qualification-mdv":
    case "census-and-current-information-mdv":
    case "health-application-status-mdv":
      return "HEALTH_INSURANCE_UPSELL" as ApplicationTypeEnum;
    case "worker-comp-application":
      return "WORKERS_COMP" as ApplicationTypeEnum;
    default:
      return "" as ApplicationTypeEnum;
  }
};
